import { useState, useEffect, useContext } from "react";
import styled, { keyframes } from "styled-components";
import { BsFillPencilFill, BsTrash } from "react-icons/bs";
import { FiPlus } from "react-icons/fi";
import { BsCalendar2Event } from "react-icons/bs";
import API from "../../API";
import AuthContext from "../../context/AuthContext";
import ModalArticle from "../Carte/Produits/Modal";
import AlertDelete from "../AlertDelete";

const nl2br = require("react-nl2br");

export default function ThemeDays() {
  const [modalShow,  setModalShow]  = useState(false);
  const [modalDelete,setModalDelete]= useState(false);
  const [events,     setEvents]     = useState([]);
  const [name,       setName]       = useState("");
  const [description,setDescription]= useState("");
  const [price,      setPrice]      = useState("");
  const [active,     setActive]     = useState({});
  const { user }                    = useContext(AuthContext);

  useEffect(() => { refresh(); }, []);

  const refresh = () => {
    API.get("/api/ThemesDays/")
      .then((res) => setEvents(res.data))
      .catch(console.error);
  };

  const onUpdate = (item) => { setActive(item); setModalShow(true); };
  const onShowDelete = (item) => { setActive(item); setModalDelete(true); };
  const onDelete = () => {
    API.delete(`/api/ThemesDays/${active.id}/`)
      .then(() => refresh())
      .catch(console.error);
    setModalDelete(false);
  };
  const onAdd = () => { setActive({}); setModalShow(true); };

  const handleSubmit = (item) => {
    if (item.id) {
      const data = {
        id:          item.id,
        name:        name        || item.name,
        description: description || item.description,
        price:       price       || item.price,
      };
      API.put(`/api/ThemesDays/${item.id}/`, data, {
        headers: { "content-type": "multipart/form-data" },
      }).then(() => refresh()).catch(console.error);
    } else {
      API.post("/api/ThemesDays/", { name, description, price }, {
        headers: { "content-type": "multipart/form-data" },
      }).then(() => refresh()).catch(console.error);
    }
    setModalShow(false);
    setName(""); setDescription(""); setPrice("");
  };

  return (
    <Page>
      <PageHeader>
        <Eyebrow>
          <BsCalendar2Event />
          <span>La Petite Charrue</span>
        </Eyebrow>
        <PageTitle>Les Journées à Thèmes</PageTitle>
        <GoldBar />
        <PageDesc>
          Découvrez nos soirées et déjeuners thématiques, organisés tout au long de l'année pour des moments conviviaux et gourmands.
        </PageDesc>
      </PageHeader>

      {events.length === 0 ? (
        <EmptyState>
          <BsCalendar2Event />
          <p>Aucun événement disponible pour le moment.</p>
          <p>Contactez-nous pour connaître nos prochaines dates.</p>
        </EmptyState>
      ) : (
        <EventsGrid>
          {events.slice(1).map((event) => (
            <EventCard key={event.id}>
              <CardAccent />

              <CardHeader>
                <EventName>{event.name}</EventName>
                {event.price && (
                  <EventPrice>{parseFloat(event.price).toFixed(2)}&nbsp;€</EventPrice>
                )}
              </CardHeader>

              {event.description && (
                <EventDesc>{nl2br(event.description)}</EventDesc>
              )}

              {user && (
                <AdminActions>
                  <ActionBtn $variant="edit" onClick={() => onUpdate(event)}>
                    <BsFillPencilFill />
                  </ActionBtn>
                  <ActionBtn $variant="delete" onClick={() => onShowDelete(event)}>
                    <BsTrash />
                  </ActionBtn>
                </AdminActions>
              )}

              <AlertDelete
                show={modalDelete && active.id === event.id}
                onHide={() => setModalDelete(false)}
                validateDelete={() => onDelete(event)}
              />
            </EventCard>
          ))}
        </EventsGrid>
      )}

      {user && (
        <AddRow>
          <AddBtn onClick={onAdd}>
            <FiPlus /> Ajouter un événement
          </AddBtn>
        </AddRow>
      )}

      <ModalArticle
        show={modalShow}
        onHide={() => setModalShow(false)}
        handleSubmit={() => handleSubmit(active)}
        activeItem={active}
        onChangeName={(e) => setName(e.target.value)}
        onChangeDescription={(e) => setDescription(e.target.value)}
        onChangePrice={(e) => setPrice(e.target.value)}
        onChangeImage={() => {}}
        services="ThemesDays"
      />
    </Page>
  );
}

/* ─── Keyframes ─── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Styled Components ─── */

const Page = styled.div`
  min-height: 100vh;
  background: var(--bg-base);
  padding-top: 72px;
`;

const PageHeader = styled.header`
  text-align: center;
  padding: 5rem 5vw 3.5rem;
  animation: ${fadeUp} 0.7s ease both;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-family: var(--font-script);
  font-size: 1rem;
  color: var(--primary);
  font-style: italic;
  letter-spacing: 0.1em;
  margin-bottom: 0.75rem;
  svg { font-size: 1rem; }
`;

const PageTitle = styled.h1`
  font-family: var(--font-heading);
  font-size: clamp(2rem, 5vw, 3.5rem);
  color: var(--cream);
  font-weight: 500;
  letter-spacing: 0.04em;
`;

const GoldBar = styled.div`
  width: 60px;
  height: 2px;
  background: linear-gradient(to right, transparent, var(--primary), transparent);
  margin: 1rem auto;
`;

const PageDesc = styled.p`
  font-size: clamp(0.95rem, 2vw, 1.1rem);
  color: var(--cream-dim);
  line-height: 1.9;
  max-width: 620px;
  margin: 0 auto;
`;

const EventsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.75rem;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 5vw 3rem;

  @media (max-width: 680px) {
    grid-template-columns: 1fr;
    padding: 0 1.25rem 2.5rem;
  }
`;

const EventCard = styled.article`
  position: relative;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  padding: 1.75rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  /* opacity explicite pour éviter tout masquage résiduel */
  opacity: 1;
  animation: ${fadeUp} 0.45s ease forwards;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(200, 151, 62, 0.35);
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.5);
  }

  /* si les animations sont désactivées, l'élément reste visible */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 1;
  }
`;

const CardAccent = styled.div`
  position: absolute;
  top: 0; left: 20%; right: 20%;
  height: 2px;
  background: linear-gradient(to right, transparent, var(--primary), transparent);
  border-radius: 0 0 4px 4px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const EventName = styled.h3`
  font-family: var(--font-heading);
  font-size: 1.15rem;
  color: var(--cream);
  font-weight: 500;
  line-height: 1.3;
  flex: 1;
`;

const EventPrice = styled.span`
  font-family: var(--font-heading);
  font-size: 1.15rem;
  color: var(--primary);
  font-weight: 600;
  white-space: nowrap;
`;

const EventDesc = styled.p`
  font-size: 0.9rem;
  color: var(--cream-muted);
  line-height: 1.8;
  letter-spacing: 0.02em;
`;

const AdminActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding-top: 0.75rem;
  border-top: 1px solid var(--border-light);
`;

const ActionBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-sm);
  border: 1px solid ${({ $variant }) =>
    $variant === "edit" ? "rgba(200,151,62,0.3)" : "rgba(220,50,50,0.3)"};
  background: transparent;
  color: ${({ $variant }) => $variant === "edit" ? "var(--primary)" : "#e05555"};
  cursor: pointer;
  transition: var(--transition);
  font-size: 0.85rem;

  &:hover {
    background: ${({ $variant }) =>
      $variant === "edit" ? "var(--primary-glow)" : "rgba(220,50,50,0.12)"};
    transform: translateY(-1px);
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 5rem 2rem;
  text-align: center;
  color: var(--cream-muted);

  svg {
    font-size: 3rem;
    color: var(--border);
    margin-bottom: 0.5rem;
  }

  p:first-of-type { font-size: 1.05rem; color: var(--cream-dim); }
  p:last-of-type  { font-size: 0.9rem; }
`;

const AddRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 5vw 4rem;
`;

const AddBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 2rem;
  background: linear-gradient(135deg, var(--primary), var(--primary-dark));
  color: var(--bg-base);
  font-family: var(--font-body);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: linear-gradient(135deg, var(--primary-light), var(--primary));
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(200,151,62,0.3);
  }
`;
